import type React from "react"
import { Box, Skeleton, Card, CardContent, styled } from "@mui/material"

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderBottom: 'none',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  "&:last-child": {
    paddingBottom: theme.spacing(2),
  },
}))

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}))

const ContentRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}))

const TextContent = styled(Box)({
  flex: 1,
  minWidth: 0,
})

const FooterRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
}))

const ActionButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}))

export const NotificationItemSkeleton: React.FC = () => {
  return (
    <StyledCard>
      <StyledCardContent>
        {/* Header with timestamp and read indicator */}
        <HeaderRow>
          <Box display="flex" alignItems="center" gap={1}>
            {/* Read status indicator */}
            <Skeleton variant="circular" width={8} height={8} />
            {/* Timestamp */}
            <Skeleton variant="text" width={80} height={16} />
          </Box>
          {/* Menu dots */}
          <Skeleton variant="circular" width={20} height={20} />
        </HeaderRow>

        {/* Main content row */}
        <ContentRow>
          {/* Avatar */}
          <Skeleton variant="circular" width={40} height={40} />

          {/* Text content */}
          <TextContent>
            {/* Title/Name */}
            <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5, fontSize: "1rem" }} />

            {/* Subtitle/Action */}
            <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1, fontSize: "0.875rem" }} />

            {/* Description/Message */}
            <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5, fontSize: "0.875rem" }} />
            <Skeleton variant="text" width="70%" height={16} sx={{ fontSize: "0.875rem" }} />
          </TextContent>
        </ContentRow>

        {/* Footer with tags and actions */}
        <FooterRow>
          {/* Tags/Categories */}
          <Box display="flex" gap={1}>
            <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />
          </Box>

          {/* Action buttons */}
          <ActionButtons>
            <Skeleton variant="rounded" width={70} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={70} height={32} sx={{ borderRadius: 1 }} />
          </ActionButtons>
        </FooterRow>
      </StyledCardContent>
    </StyledCard>
  )
}

export default NotificationItemSkeleton;